import React, { useState } from "react";
import { BRAND } from "@/lib/brand";
import { RequireStaff } from "@/components/RequireStaff";
import { useStaffDeals } from "@/hooks/useStaffDeals";
import { StaffKpiCards } from "@/components/staff/StaffKpiCards";
import { StaffDealsTable } from "@/components/staff/StaffDealsTable";
import { StaffPipelineChart } from "@/components/staff/StaffPipelineChart";
import { DealMeetingsSection } from "@/components/meetings/DealMeetingsSection";
import { AdminContactRequests } from "@/components/dashboard/ContactRequestsTracker";
import { useContactRequestNotifications } from "@/hooks/useContactRequestNotifications";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ListTodo, 
  Inbox, 
  FolderOpen, 
  GitBranch, 
  Search, 
  Filter,
  RefreshCw,
  Video,
  Send
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { MorningBriefing } from "@/components/staff/MorningBriefing";

type TabType = 'todo' | 'triage' | 'all' | 'pipeline' | 'meetings' | 'contacts';

const translations = {
  en: {
    title: "Staff Dashboard",
    subtitle: "Tasks, assigned deals, messages, calls.",
    tabs: {
      todo: "My To-Do",
      triage: "Files to Sort",
      all: "Active Files",
      pipeline: "Pipeline by Status",
      meetings: "Meetings",
      contacts: "Contact Requests"
    },
    filters: {
      search: "Search...",
      priority: "Priority",
      status: "Status",
      role: "Client Role",
      all: "All",
      high: "High",
      normal: "Normal",
      low: "Low"
    },
    refresh: "Refresh",
    todoTitle: "My To-Do: Operational Priorities",
    triageTitle: "Files to Sort: New Submissions",
    triageDesc: "These deals are not assigned to any Staff. Take one to start the review.",
    allTitle: "All Active Files",
    pipelineTitle: "Pipeline by Status",
    meetingsTitle: "Meetings",
    loadingTodo: "Loading your To-Do...",
    loadingTriage: "Checking new deals...",
    loadingDeals: "Loading files..."
  },
  fr: {
    title: "Tableau de Bord Staff",
    subtitle: "Tâches, dossiers assignés, messages, appels.",
    tabs: {
      todo: "Ma To-Do",
      triage: "Dossiers à Trier",
      all: "Dossiers en Cours",
      pipeline: "Pipeline par Statut",
      meetings: "Réunions",
      contacts: "Demandes de Contact"
    },
    filters: {
      search: "Rechercher...",
      priority: "Priorité",
      status: "Statut",
      role: "Rôle Client",
      all: "Tous",
      high: "Haute",
      normal: "Normale",
      low: "Basse"
    },
    refresh: "Actualiser",
    todoTitle: "Ma To-Do : Priorités Opérationnelles",
    triageTitle: "Dossiers à Trier : Nouvelles Soumissions",
    triageDesc: "Ces deals ne sont assignés à aucun Staff. Prenez-en un en charge pour commencer la revue.",
    allTitle: "Tous les Dossiers Actifs",
    pipelineTitle: "Pipeline par Statut",
    meetingsTitle: "Réunions",
    loadingTodo: "Chargement de votre To-Do...",
    loadingTriage: "Vérification des nouveaux Deals...",
    loadingDeals: "Chargement des dossiers..."
  }
};

export default function StaffDashboard() {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [activeTab, setActiveTab] = useState<TabType>('todo');
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  
  const { deals, loading, kpis, pipelineStats, takeDeal } = useStaffDeals(activeTab as any);

  // Real-time notifications for new contact requests
  useContactRequestNotifications({ role: 'staff' });

  // Filter deals based on search and priority
  const filteredDeals = deals.filter(deal => {
    const title = deal.title || "Untitled Deal";
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          deal.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || deal.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  return (
    <RequireStaff>
      <div className="space-y-6 font-['Geist',sans-serif] bg-[#0F1115] min-h-screen p-6">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{t.title}</h1>
            <p className="text-gray-400">{t.subtitle}</p>
          </div>
          <Button 
            onClick={() => window.location.reload()}
            variant="outline" 
            size="sm"
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t.refresh}
          </Button>
        </header>

        {/* Phase 4 — Briefing matinal */}
        <MorningBriefing />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabType)} className="space-y-6">
          <TabsList className="bg-gray-800/50 border border-gray-700 p-1 flex-wrap h-auto">
            <TabsTrigger 
              value="todo" 
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400"
            >
              <ListTodo className="w-4 h-4 mr-2" />
              {t.tabs.todo}
            </TabsTrigger>
            <TabsTrigger 
              value="triage"
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400"
            >
              <Inbox className="w-4 h-4 mr-2" />
              {t.tabs.triage}
              {kpis.triageCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full animate-pulse">
                  {kpis.triageCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="all"
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              {t.tabs.all}
            </TabsTrigger>
            <TabsTrigger 
              value="pipeline"
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              {t.tabs.pipeline}
            </TabsTrigger>
            <TabsTrigger 
              value="meetings"
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400"
            >
              <Video className="w-4 h-4 mr-2" />
              {t.tabs.meetings}
            </TabsTrigger>
            <TabsTrigger 
              value="contacts"
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400"
            >
              <Send className="w-4 h-4 mr-2" />
              {t.tabs.contacts}
            </TabsTrigger>
          </TabsList>

          {/* KPI Cards */}
          {activeTab !== 'contacts' && <StaffKpiCards kpis={kpis} view={activeTab as any} />}

          {/* Filters */}
          {activeTab !== 'pipeline' && (
            <div className="flex flex-col sm:flex-row gap-3 bg-gray-800/30 p-4 rounded-xl border border-gray-700/50">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder={t.filters.search}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full sm:w-[150px] bg-gray-900 border-gray-700 text-white">
                  <Filter className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder={t.filters.priority} />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all" className="text-gray-300">{t.filters.all}</SelectItem>
                  <SelectItem value="high" className="text-gray-300">{t.filters.high}</SelectItem>
                  <SelectItem value="normal" className="text-gray-300">{t.filters.normal}</SelectItem>
                  <SelectItem value="low" className="text-gray-300">{t.filters.low}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Tab Content */}
          <TabsContent value="todo" className="space-y-4 mt-0">
            <h2 className="text-lg font-semibold text-white">{t.todoTitle}</h2>
            {loading ? (
              <div className="bg-gray-800/50 rounded-xl p-8 text-center">
                <p className="text-gray-400 animate-pulse">{t.loadingTodo}</p>
              </div>
            ) : (
              <StaffDealsTable deals={filteredDeals} view="todo" />
            )}
          </TabsContent>

          <TabsContent value="triage" className="space-y-4 mt-0">
            <div>
              <h2 className="text-lg font-semibold text-white">{t.triageTitle} ({filteredDeals.length})</h2>
              <p className="text-sm text-gray-500">{t.triageDesc}</p>
            </div>
            {loading ? (
              <div className="bg-gray-800/50 rounded-xl p-8 text-center">
                <p className="text-gray-400 animate-pulse">{t.loadingTriage}</p>
              </div>
            ) : (
              <StaffDealsTable deals={filteredDeals} view="triage" onTakeDeal={takeDeal} />
            )}
          </TabsContent>

          <TabsContent value="all" className="space-y-4 mt-0">
            <h2 className="text-lg font-semibold text-white">{t.allTitle}</h2>
            {loading ? (
              <div className="bg-gray-800/50 rounded-xl p-8 text-center">
                <p className="text-gray-400 animate-pulse">{t.loadingDeals}</p>
              </div>
            ) : (
              <StaffDealsTable deals={filteredDeals} view="all" />
            )}
          </TabsContent>

          <TabsContent value="pipeline" className="space-y-4 mt-0">
            <h2 className="text-lg font-semibold text-white">{t.pipelineTitle}</h2>
            <StaffPipelineChart data={pipelineStats} />
          </TabsContent>

          <TabsContent value="meetings" className="space-y-4 mt-0">
            <h2 className="text-lg font-semibold text-white">{t.meetingsTitle}</h2>
            <DealMeetingsSection />
          </TabsContent>

          <TabsContent value="contacts" className="space-y-4 mt-0">
            <h2 className="text-lg font-semibold text-white">{language === 'fr' ? 'Demandes de Contact' : 'Contact Requests'}</h2>
            <AdminContactRequests language={language} />
          </TabsContent>
        </Tabs>
      </div>
    </RequireStaff>
  );
}
